import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Col, Form, FormInstance, Row } from "antd";
import {
  CARD_TABLE_COLUMN_UNIT_HEIGHT,
  CardTable,
} from "components/card-table";
import { Spinal } from "components/spinal/Spinal";
import { CustomInputNumber } from "components/Form/CustomInputNumber";
import { layout } from "helpers/arealen-layout";
import { roundAndDisplayWithDecimals } from "helpers/logica/round-and-display-with-decimals";
import { roundToDecimals } from "helpers/logica/round-to-decimals";
import { FC } from "react";
import { achtergrondInfo } from "../bodem-overig/info/achtergrondInfo";
import { PercelenModel } from "./models/percelenMock";
import { flattenState, mapToLayout } from "./mapper";
import { fosfaatklasses, grondgebruiken, grondsoorten, kenmerkenGronden } from "./settings";
import styles from "./styles.module.scss";

interface ArealenFosfaatGrondsoortenProps {
  isRefreshing: boolean;
  isKlwReadOnly: boolean;
  isFormChanged: boolean;
  isEditable: boolean;
  isLoading: boolean;
  form: FormInstance;
  herbereken: () => void;
  klwJaar: number;
  bothKotoSettingsOff: boolean;
}
export const ArealenFosfaatGrondsoorten: FC<
  ArealenFosfaatGrondsoortenProps
> = ({
  isRefreshing,
  isKlwReadOnly,
  isEditable,
  form,
  isLoading,
  isFormChanged,
  herbereken,
  klwJaar,
  bothKotoSettingsOff
}) => {
  return (
    <CardTable>
      <CardTable.Header>
        <CardTable.Header.Title infoContent={achtergrondInfo()}>
          Arealen fosfaatklasses en grondsoorten
        </CardTable.Header.Title>
        <CardTable.Header.ColumnTitles>
          <Row gutter={12}>
            <Col span={layout.title}></Col>
            <Col
              span={layout.fosfaatInterval * 6}
              style={{ textAlign: "left" }}
            >
              <h3>Fosfaatklasse</h3>
            </Col>
            <Col span={layout.break} />
            <Col span={layout.grondInterval * 6} style={{ textAlign: "left" }}>
              <h3>Grondsoorten</h3>
            </Col>
          </Row>
          <Row gutter={12} align="bottom">
            <Col span={layout.title}>
              Grondgebruik
              <CardTable.ColumnUnit>[ha]</CardTable.ColumnUnit>
            </Col>
            {/* FOSFAAT KOLOMMEN */}
            <Col span={layout.fosfaatInterval} className="text-right">
              Hoog
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.fosfaatInterval} className="text-right">
              Ruim
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.fosfaatInterval} className="text-right">
              Neutraal
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.fosfaatInterval} className="text-right">
              Laag
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.fosfaatInterval} className="text-right">
              Arm
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col
              span={layout.fosfaatInterval}
              className="text-right"
              style={{ fontWeight: "bold" }}
            >
              Totaal
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.break} />
            {/* GROND KOLOMMEN */}
            <Col span={layout.grondInterval} className="text-right">
              Veen
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.grondInterval} className="text-right">
              Klei
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
            <Col span={layout.grondInterval} className="text-right">
              Zand
              <CardTable.ColumnUnit>
                {" "}
                [I{klwJaar <= 2022 && "I"}-V]
              </CardTable.ColumnUnit>
            </Col>
            <Col span={layout.grondInterval} className="text-right">
              Zand
              <CardTable.ColumnUnit> [VI]</CardTable.ColumnUnit>
            </Col>
            <Col span={layout.grondInterval} className="text-right">
              Zand
              <CardTable.ColumnUnit> [VII-VIII]</CardTable.ColumnUnit>
            </Col>
            <Col
              span={layout.grondInterval}
              className="text-right"
              style={{ fontWeight: "bold" }}
            >
              Totaal
              <div style={{ height: CARD_TABLE_COLUMN_UNIT_HEIGHT }} />
            </Col>
          </Row>
        </CardTable.Header.ColumnTitles>
      </CardTable.Header>
      <div className={isFormChanged && !isEditable ? styles.overlay : ""}>
        <CardTable.Body>
          <Form form={form}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => {
                return (
                  prev.grond?.fosfaattoestandOppervlakten !==
                    cur.grond?.fosfaattoestandOppervlakten ||
                  prev.grondsoorten?.verdelingGrondsoorten !==
                    cur.grondsoorten?.verdelingGrondsoorten
                );
              }}
            >
              {({ getFieldValue }) => {
                const fosfaattoestandOppervlakten: PercelenModel["grond"]["fosfaattoestandOppervlakten"] =
                  getFieldValue(["grond", "fosfaattoestandOppervlakten"]);
                const verdelingGrondsoorten: PercelenModel["grondsoorten"]["verdelingGrondsoorten"] =
                  getFieldValue(["grondsoorten", "verdelingGrondsoorten"]);
                let filteredVerdelingGrondsoorten: PercelenModel["grondsoorten"]["verdelingGrondsoorten"] =
                  [];
                if (verdelingGrondsoorten)
                  filteredVerdelingGrondsoorten = verdelingGrondsoorten.filter(
                    (
                      f: PercelenModel["grondsoorten"]["verdelingGrondsoorten"][0]
                    ) => f.grondsoortcategorieId !== 6
                  );
                const arealenFosfaatGrondsoorten = mapToLayout(
                  fosfaattoestandOppervlakten,
                  filteredVerdelingGrondsoorten,
                );
                const arealenFosfaatGrondsoortenObject = flattenState(
                  arealenFosfaatGrondsoorten
                );
                const exists: boolean =
                  getFieldValue(["grasland", "fosfaat", 4]) !== undefined;
                if (!exists && fosfaattoestandOppervlakten)
                  form.setFieldsValue(arealenFosfaatGrondsoortenObject);
                return (
                  <Spinal
                    initializing={
                      !fosfaattoestandOppervlakten ||
                      !filteredVerdelingGrondsoorten ||
                      !arealenFosfaatGrondsoorten ||
                      !kenmerkenGronden 
                    }
                    loading={isRefreshing || isLoading}
                  >
                    {arealenFosfaatGrondsoorten.map((a, indexRow) => (
                      <Row
                        key={indexRow}
                        gutter={12}
                        style={{
                          padding: "5px 0px 5px 0px",
                          marginBottom: 5,
                        }}
                        align="bottom"
                      >
                        <Col span={layout.title}>
                          <Form.Item
                            name={[a.type, "type"]}
                            initialValue={a.type}
                            hidden
                          />
                          <Form.Item
                            name={[a.type, "fosfaatKey"]}
                            initialValue={a.key}
                            hidden
                          />
                          <div
                            style={{ lineHeight: "32px" }}
                          >{`${a.type[0].toUpperCase()}${a.type.slice(
                            1
                          )}`}</div>
                        </Col>

                        {/* FOSFAAT KOLOMMEN */}
                        {fosfaatklasses.map((klasse, index) => (
                          <Col
                            key={index}
                            span={layout.fosfaatInterval}
                            className="text-right"
                          >
                            <Form.Item name={[a.type, "fosfaat", klasse.id]}>
                              {isEditable &&
                              a.fosfaat[klasse.id] !== undefined ? (
                                <CustomInputNumber
                                  disabled={isKlwReadOnly}
                                  className="input-text-right font11"
                                  min={0}
                                  max={500}
                                  precision={2}
                                />
                              ) : (
                                <div style={{ paddingRight: 13 }}>
                                  {roundAndDisplayWithDecimals(2)(
                                    a.fosfaat[klasse.id]
                                  ) || "Nvt"}
                                </div>
                              )}
                            </Form.Item>
                          </Col>
                        ))}
                        <Col
                          span={layout.fosfaatInterval}
                          className="text-right"
                          style={{ fontWeight: "bold" }}
                        >
                          <Form.Item
                            shouldUpdate={(prev, cur) => {
                              return (
                                prev[a.type]?.fosfaat !==
                                  cur[a.type]?.fosfaat ||
                                prev[a.type]?.grondsoorten !==
                                  cur[a.type]?.grondsoorten
                              );
                            }}
                          >
                            {({ getFieldValue }) => {
                              const fosfaatValues = getFieldValue([
                                a.type,
                                "fosfaat",
                              ]);
                              let fosfaatTotaal = 0;
                              let grondTotaal = 0;
                              if (fosfaatValues) {
                                fosfaatTotaal = Object.values<number>(
                                  fosfaatValues
                                )?.reduce((a, b) => a + b, 0);
                                const grondValues = getFieldValue([
                                  a.type,
                                  "grondsoorten",
                                ]);
                                grondTotaal = Object.values<number>(
                                  grondValues
                                )?.reduce((a, b) => a + b, 0);
                              }
                              const isEqual =
                                roundToDecimals(2)(fosfaatTotaal) ===
                                roundToDecimals(2)(grondTotaal);
                              return (
                                <div
                                  style={{
                                    color: isEqual ? "" : "red",
                                    paddingRight: 13,
                                  }}
                                >
                                  {roundAndDisplayWithDecimals(2)(
                                    fosfaatTotaal
                                  )}
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Col>
                        <Col span={layout.break} />
                        {/* GROND KOLOMMEN */}
                        {grondsoorten.map((grond, index) => (
                         <Col
                           key={index}
                           span={layout.grondInterval}
                           className="text-right"
                         >
                           <Form.Item
                             name={[a.type, "grondsoorten", grond.id]}
                           >
                             {bothKotoSettingsOff &&
                             a.grondsoorten[grond.id] !== undefined ? (
                               <CustomInputNumber
                                 disabled={isKlwReadOnly}
                                 className="input-text-right font11"
                                 min={0}
                                 max={500}
                                 precision={2}
                               />
                             ) : (
                               <div style={{ paddingRight: 13 }}>
                                  {roundAndDisplayWithDecimals(2)(
                                    a.grondsoorten[grond.id]
                                  ) || "Nvt"}
                               </div>
                             )}
                           </Form.Item>
                         </Col>           
                        ))}
                        <Col
                          span={layout.grondInterval}
                          className="text-right"
                          style={{ fontWeight: "bold" }}
                        >
                          <Form.Item
                            shouldUpdate={(prev, cur) => {
                              return (
                                prev[a.type]?.fosfaat !==
                                  cur[a.type]?.fosfaat ||
                                prev[a.type]?.grondsoorten !==
                                  cur[a.type]?.grondsoorten
                              );
                            }}
                          >
                            {({ getFieldValue }) => {
                              const fosfaatValues = getFieldValue([
                                a.type,
                                "fosfaat",
                              ]);
                              let fosfaatTotaal = 0;
                              let grondTotaal = 0;
                              if (fosfaatValues) {
                                fosfaatTotaal = Object.values<number>(
                                  fosfaatValues
                                ).reduce((a, b) => a + b, 0);
                                const grondValues = getFieldValue([
                                  a.type,
                                  "grondsoorten",
                                ]);
                                grondTotaal = Object.values<number>(
                                  grondValues
                                ).reduce((a, b) => a + b, 0);
                              }
                              const isEqual =
                                roundToDecimals(2)(fosfaatTotaal) ===
                                roundToDecimals(2)(grondTotaal);
                              return (
                                <div
                                  style={{
                                    color: isEqual ? "" : "red",
                                    paddingRight: 13,
                                  }}
                                >
                                  {roundAndDisplayWithDecimals(2)(grondTotaal)}
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                    {/* Totaal */}
                    <Row
                      style={{
                        padding: "5px 0px 5px 0px",
                        marginBottom: 0,
                      }}
                      gutter={12}
                      align="bottom"
                    >
                      <Col span={layout.title} style={{ fontWeight: "bold" }}>
                        Totaal
                      </Col>
                      {/* FOSFAAT KOLOMMEN */}
                      {fosfaatklasses.map((soort, index) => (
                        <Col
                          key={index}
                          span={layout.fosfaatInterval}
                          style={{ fontWeight: "bold" }}
                          className="text-right"
                        >
                          <Form.Item shouldUpdate>
                            {({ getFieldValue }) => {
                              const values: number[] = [];
                              grondgebruiken.forEach((grond) => {
                                const nr = getFieldValue([
                                  grond,
                                  "fosfaat",
                                  soort.id,
                                ]);
                                values.push(nr);
                              });

                              const summedValue = Object.values<number>(
                                values
                              ).reduce((a, b) => a + b, 0);
                              return isEditable ? (
                                roundAndDisplayWithDecimals(2)(summedValue)
                              ) : (
                                <div style={{ paddingRight: 13 }}>
                                  {roundAndDisplayWithDecimals(2)(summedValue)}
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Col>
                      ))}
                      <Col
                        span={layout.fosfaatInterval}
                        className="text-right"
                        style={{ fontWeight: "bold" }}
                      ></Col>
                      <Col span={layout.break} />
                      {/* GROND KOLOMMEN */}
                      {grondsoorten.map((soort, index) => (
                        <Col
                          key={index}
                          span={layout.grondInterval}
                          style={{ fontWeight: "bold" }}
                          className="text-right"
                        >
                          <Form.Item shouldUpdate>
                            {({ getFieldValue }) => {
                              const values: number[] = [];
                              grondgebruiken.forEach((grond) => {
                                const nr = getFieldValue([
                                  grond,
                                  "grondsoorten",
                                  soort.id,
                                ]);
                                values.push(nr);
                              });

                              const summedValue = Object.values<number>(
                                values
                              ).reduce((a, b) => a + b, 0);
                              return isEditable ? (
                                roundAndDisplayWithDecimals(2)(summedValue)
                              ) : (
                                  <div style={{ paddingRight: 13 }}>
                                  {roundAndDisplayWithDecimals(2)(summedValue)}
                                </div>
                              );
                            }}
                          </Form.Item>
                        </Col>                       
                      ))}
                    </Row>{" "}
                  </Spinal>
                );
              }}
            </Form.Item>
          </Form>
          {isFormChanged && !isEditable && (
            <Button
              shape="round"
              type="primary"
              className={styles.overlayCenter}
              icon={<FontAwesomeIcon icon={"sync"} />}
              onClick={herbereken}
              loading={isRefreshing}
            >
              Opnieuw berekenen
            </Button>
          )}
        </CardTable.Body>
      </div>
    </CardTable>
  );
};
